// <s-box-builder>: "Componi la tua box". Customers pick single products; the right gift box is chosen
// automatically (largest minimum size among the items, or by count: up to 4 = small, up to 6 = medium,
// more = large). One click adds every item plus the box to the cart, all tagged with the same box number.
class SBoxBuilder extends HTMLElement {
  #ac;
  #boxes = [];
  #qtys = new Map();
  #money;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    try { this.#boxes = JSON.parse(this.dataset.boxes || '[]'); } catch { this.#boxes = []; }
    this.#money = new Intl.NumberFormat(this.dataset.locale || 'it', {
      style: 'currency', currency: this.dataset.currency || 'EUR',
    });

    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const item = target?.closest('[data-box-item]');
      if (target?.closest('[data-box-another]')) {
        this.querySelector('[data-box-success]').hidden = true;
        this.querySelector('[data-box-item]')?.scrollIntoView({ block: 'start' });
        return;
      }
      if (target?.closest('[data-box-submit]')) { this.#submit(); return; }
      if (!item) return;
      if (target.closest('[data-box-add]')) this.#setQty(item, 1);
      else if (target.closest('[data-box-plus]')) this.#setQty(item, this.#qty(item) + 1);
      else if (target.closest('[data-box-minus]')) this.#setQty(item, this.#qty(item) - 1);
    }, opts);

    this.addEventListener('change', (event) => {
      const select = event.target instanceof HTMLSelectElement ? event.target : null;
      const item = select?.closest('[data-box-item]');
      if (!item) return;
      const option = select.selectedOptions[0];
      const image = item.querySelector('img');
      if (image && option?.dataset.image) {
        image.removeAttribute('srcset');
        image.src = option.dataset.image;
        image.alt = `${item.dataset.title} (${option.dataset.label || ''})`;
      }
      item.querySelector('[data-box-price]').textContent = this.#format(Number(option?.dataset.price || 0));
      this.#syncItem(item);
    }, opts);

    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #variantEl(item) {
    const el = item.querySelector('[data-box-variant]');
    return el instanceof HTMLSelectElement ? el.selectedOptions[0] : el;
  }

  // Every variant keeps its own quantity, so a customer can put two different colours of the same product in one box.
  #variantOptions(item) {
    const el = item.querySelector('[data-box-variant]');
    return el instanceof HTMLSelectElement ? [...el.options] : el ? [el] : [];
  }

  #qty(item) {
    return this.#qtys.get(String(this.#variantEl(item)?.value || '')) || 0;
  }

  #max(item) {
    const max = Number(this.#variantEl(item)?.dataset.max ?? 99);
    return Number.isFinite(max) ? Math.max(0, max) : 99;
  }

  #showError(message) {
    const error = this.querySelector('[data-box-error]');
    error.textContent = message;
    error.hidden = false;
    document.dispatchEvent(new CustomEvent('s-notification:add', { detail: { message, variant: 'notice', duration: 7000 } }));
  }

  #syncItem(item) {
    const qty = this.#qty(item);
    const addButton = item.querySelector('[data-box-add]');
    const stepper = item.querySelector('[data-box-stepper]');
    if (addButton) addButton.hidden = qty > 0;
    if (stepper) stepper.hidden = qty === 0;
    const qtyLabel = item.querySelector('[data-box-qty]');
    if (qtyLabel) qtyLabel.textContent = String(qty);
    const plus = item.querySelector('[data-box-plus]');
    if (plus) plus.disabled = qty >= this.#max(item);
  }

  #setQty(item, qty) {
    const variantId = String(this.#variantEl(item)?.value || '');
    const current = this.#qty(item);
    const next = Math.max(0, Math.min(qty, this.#max(item)));
    const proposed = this.#selected().filter((line) => line.id !== variantId);
    proposed.push({ id: variantId, qty: next, size: Number(item.dataset.size || 1), candle: item.dataset.candle === 'true', bear: item.dataset.bear === 'true' });

    if (next > current) {
      const bulky = proposed.reduce((sum, line) => sum + (line.size >= 2 ? line.qty : 0), 0);
      if (bulky > 2) { this.#showError(this.dataset.capacityError); return; }
      const hasPen = proposed.some((line) => line.size === 3 && line.qty > 0);
      const candles = proposed.filter((line) => line.candle && line.qty > 0);
      const candleCount = candles.reduce((sum, line) => sum + line.qty, 0);
      const candleLimit = candles.some((line) => !line.bear) ? 2 : 3;
      if (hasPen && candleCount > candleLimit) { this.#showError(this.dataset.candleError); return; }
    }

    this.querySelector('[data-box-error]').hidden = true;
    if (next > 0) this.#qtys.set(variantId, next);
    else this.#qtys.delete(variantId);
    this.#syncItem(item);
    this.#render();
  }

  #selected() {
    return [...this.querySelectorAll('[data-box-item]')].flatMap((item) =>
      this.#variantOptions(item)
        .filter((variant) => (this.#qtys.get(String(variant.value)) || 0) > 0)
        .map((variant) => {
          const label = variant.dataset.label;
          return {
            id: String(variant.value),
            qty: this.#qtys.get(String(variant.value)),
            price: Number(variant.dataset.price || 0),
            size: Number(item.dataset.size || 1),
            candle: item.dataset.candle === 'true',
            bear: item.dataset.bear === 'true',
            type: item.dataset.type || '',
            title: label ? `${item.dataset.title} (${label})` : item.dataset.title,
          };
        }));
  }

  #boxFor(selected) {
    const count = selected.reduce((sum, line) => sum + line.qty, 0);
    if (!count) return null;
    const bySize = Math.max(...selected.map((line) => line.size));
    const byCount = count <= 4 ? 1 : count <= 6 ? 2 : 3;
    const bulkyCount = selected.reduce((sum, line) => sum + (line.size === 2 ? line.qty : 0), 0);
    // Rose Bear candles (merchant, 2026-10-11):
    // - bears alone: up to 3 in the small box, 4 or more need the medium;
    // - bears with other products need at least the medium (Essential Box style: mask + perfume + keychain + 1 bear,
    //   or mask + perfume + 2 bears). Face masks are flat: up to 6 masks fit and they don't take a "piece" slot.
    //   So with bears, the medium holds up to 4 non-mask pieces (mask + perfume + keychain + 2 bears = medium); more goes to the large box.
    const bearCount = selected.reduce((sum, line) => sum + (line.bear ? line.qty : 0), 0);
    const maskCount = selected.reduce((sum, line) => sum + (line.type === 'Maschere Viso' ? line.qty : 0), 0);
    const otherCount = count - bearCount;
    const piecesCount = count - maskCount;
    let bySizeAndCount = Math.max(bySize, bulkyCount >= 2 ? 3 : 1);
    if (bearCount > 0) {
      let byBears = 1;
      if (otherCount === 0) byBears = bearCount <= 3 ? 1 : 2;
      else byBears = piecesCount <= 4 && maskCount <= 6 ? 2 : 3;
      bySizeAndCount = Math.max(bySizeAndCount, byBears);
    } else {
      bySizeAndCount = Math.max(bySizeAndCount, byCount);
    }
    const size = bySizeAndCount;
    return this.#boxes.find((box) => box.size === size) || null;
  }

  #format(cents) {
    return this.#money.format(cents / 100);
  }

  #render() {
    const selected = this.#selected();
    const count = selected.reduce((sum, line) => sum + line.qty, 0);
    const box = this.#boxFor(selected);
    const itemsTotal = selected.reduce((sum, line) => sum + line.price * line.qty, 0);
    const total = itemsTotal + (box ? box.price : 0);

    const summary = this.querySelector('[data-box-summary]');
    const detail = this.querySelector('[data-box-detail]');
    if (!count) {
      summary.textContent = this.dataset.empty || '';
      detail.textContent = '';
    } else {
      const items = count === 1
        ? this.dataset.itemsOne
        : (this.dataset.itemsOther || '').replace('[count]', String(count));
      summary.textContent = `${items} · ${(this.dataset.boxLabel || '').replace('[size]', box?.name || '')}`;
      detail.textContent = box ? `${box.dims} · ${this.#format(box.price)}` : '';
    }
    this.querySelector('[data-box-total]').textContent = this.#format(total);
    this.querySelector('[data-box-submit]').disabled = !count;

    this.querySelectorAll('[data-box-size]').forEach((card) => {
      card.toggleAttribute('data-active', Boolean(box) && Number(card.dataset.boxSize) === box.size);
    });
  }

  async #submit() {
    const selected = this.#selected();
    const box = this.#boxFor(selected);
    if (!box || this.getAttribute('aria-busy') === 'true') return;

    const button = this.querySelector('[data-box-submit]');
    this.setAttribute('aria-busy', 'true');
    button.disabled = true;

    const boxNumber = String(Math.floor(1000 + Math.random() * 9000));
    const boxKey = this.dataset.lineProperty || 'Box';
    const contents = selected.map((line) => `${line.qty} × ${line.title}`).join(', ');
    const items = [
      ...selected.map((line) => ({ id: line.id, quantity: line.qty, properties: { [boxKey]: `n. ${boxNumber}` } })),
      {
        id: String(box.variant),
        quantity: 1,
        properties: { [boxKey]: `n. ${boxNumber}`, [this.dataset.contentsProperty || 'Contenuto']: contents },
      },
    ];

    try {
      await this.#addToCart(items);
      this.#qtys.clear();
      this.querySelectorAll('[data-box-item]').forEach((item) => this.#syncItem(item));
      this.#render();
      this.removeAttribute('aria-busy');
      const success = this.querySelector('[data-box-success]');
      success.hidden = false;
      success.scrollIntoView({ block: 'center' });
    } catch (error) {
      const message = error?.message || this.dataset.error || '';
      document.dispatchEvent(new CustomEvent('s-notification:add', {
        detail: { message, variant: 'error', duration: 6000 },
      }));
      this.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  async #addToCart(items) {
    if (window.Shopify?.actions?.updateCart) {
      const result = await window.Shopify.actions.updateCart({
        lines: items.map((item) => ({
          merchandiseId: item.id,
          quantity: item.quantity,
          attributes: Object.entries(item.properties).map(([key, value]) => ({ key, value })),
        })),
      }, { event: { context: 'product', detail: { source: 's-box-builder' } } });
      const userError = result?.userErrors?.[0]?.message;
      if (userError) throw new Error(userError);
      return;
    }

    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.description || data.message || this.dataset.error || '');
    }
  }
}

if (!customElements.get('s-box-builder')) customElements.define('s-box-builder', SBoxBuilder);
